import type { QueryCache } from '@tanstack/react-query';

import { knownRows, type Fields } from './known-rows';

const isObject = (v: unknown): v is Fields => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Row fields minus the nested collections a detail response embeds. */
function withoutNested(row: Fields, nested: readonly string[]): Fields {
  const out: Fields = {};
  for (const [k, v] of Object.entries(row)) if (!nested.includes(k)) out[k] = v;
  return out;
}

/**
 * Teaches `knownRows` from the responses of the two detail endpoints.
 * Only data that came from the server counts: `setQueryData` (optimistic
 * updates, rollbacks) is `manual` and is ignored.
 */
export function ingestQueryData(queryKey: readonly unknown[], data: unknown): void {
  if (!isObject(data)) return;
  if (queryKey[0] === 'tour' && typeof data.id === 'string' && Array.isArray(data.panoramas)) {
    knownRows.tour.seed(data.id, withoutNested(data, ['panoramas', 'markers', 'folders']));
    for (const p of data.panoramas) {
      if (isObject(p) && typeof p.id === 'string') knownRows.panorama.seed(p.id, p);
    }
  } else if (queryKey[0] === 'panorama-editor' && isObject(data.panorama)) {
    const pano = data.panorama;
    if (typeof pano.id === 'string') knownRows.panorama.seed(pano.id, pano);
    if (Array.isArray(data.markers)) {
      for (const m of data.markers) {
        if (isObject(m) && typeof m.id === 'string') knownRows.marker.seed(m.id, m);
      }
    }
    if (Array.isArray(data.panoramas)) {
      for (const s of data.panoramas) {
        if (isObject(s) && typeof s.id === 'string') knownRows.panorama.seed(s.id, s);
      }
    }
  }
}

export function trackServerRows(cache: QueryCache): () => void {
  return cache.subscribe(event => {
    if (event.type !== 'updated' || event.action.type !== 'success' || event.action.manual) return;
    ingestQueryData(event.query.queryKey, event.action.data);
  });
}
